import { BellRing, Check, Clock3, KeyRound, Mail, MessageCircle, MonitorSmartphone, Phone, RefreshCw, Save, Send, ShieldCheck, Smartphone, Trash2, TriangleAlert } from "lucide-react";
import { useEffect, useState, type ChangeEvent, type FormEvent } from "react";
import { useSession } from "../auth/SessionContext";
import { Button, Card, DataAge, EmptyState, ErrorState, Field, InlineNotice, LoadingBlock, PageHeader } from "../components/ui";
import { useDocumentTitle } from "../hooks/useDocumentTitle";
import { useRemote } from "../hooks/useRemote";
import { apiRequest, queryString } from "../lib/api";
import { isNotificationChannelVisibleInAdmin, SHOW_TWILIO_ADMIN } from "../lib/feature-flags";
import { CHANNEL_LABELS, DELIVERY_LABELS } from "../lib/format";
import { subscribeToPush, supportsWebPush } from "../lib/push";
import type { CursorPage, DeliveryChannel, NotificationConfig, NotificationDelivery } from "../types";

interface ConfigForm {
  email: string;
  smsPhone: string;
  whatsappPhone: string;
  summaryTime: string;
  timezone: string;
  smtpHost: string;
  smtpPort: string;
  smtpUser: string;
  smtpPassword: string;
  smtpFrom: string;
  twilioAccountSid: string;
  twilioAuthToken: string;
  twilioSmsFrom: string;
  twilioWhatsappFrom: string;
  templateOpened: string;
  templateRepeated: string;
  templateRecovered: string;
}

const EMPTY_FORM: ConfigForm = {
  email: "", smsPhone: "", whatsappPhone: "", summaryTime: "08:00", timezone: "America/Argentina/Buenos_Aires",
  smtpHost: "", smtpPort: "587", smtpUser: "", smtpPassword: "", smtpFrom: "",
  twilioAccountSid: "", twilioAuthToken: "", twilioSmsFrom: "", twilioWhatsappFrom: "",
  templateOpened: "", templateRepeated: "", templateRecovered: "",
};

export function NotificationAdminPage() {
  useDocumentTitle("Notificaciones");
  const { csrfToken } = useSession();
  const configRemote = useRemote(() => apiRequest<NotificationConfig>("/api/v1/admin/notifications/config"), [], 60_000);
  const deliveries = useRemote(() => apiRequest<CursorPage<NotificationDelivery>>(`/api/v1/admin/notifications/deliveries${queryString({ limit: 50 })}`), [], 30_000);
  const [form, setForm] = useState<ConfigForm>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<DeliveryChannel | null>(null);
  const [pushBusy, setPushBusy] = useState(false);
  const [message, setMessage] = useState<{ tone: "success" | "danger" | "warning"; title: string; body: string } | null>(null);
  const [pushLabel, setPushLabel] = useState(() => `${navigator.platform || "Navegador"} · ${new Date().toLocaleDateString("es-AR")}`);
  const notificationsDisabled = configRemote.data?.mode === "disabled";
  const visibleDeliveries = deliveries.data?.items.filter((delivery) => isNotificationChannelVisibleInAdmin(delivery.channel)) ?? [];

  useEffect(() => {
    const config = configRemote.data;
    if (!config) return;
    setForm((current) => ({
      ...current,
      email: config.recipient.email,
      smsPhone: config.recipient.smsPhone,
      whatsappPhone: config.recipient.whatsappPhone,
      summaryTime: config.summary.time,
      timezone: config.summary.timezone,
      smtpHost: config.smtp.host,
      smtpPort: String(config.smtp.port),
      smtpUser: config.smtp.username,
      smtpFrom: config.smtp.from,
      twilioAccountSid: config.twilio.accountSid,
      twilioSmsFrom: config.twilio.smsFrom,
      twilioWhatsappFrom: config.twilio.whatsappFrom,
      templateOpened: config.twilio.templates.opened,
      templateRepeated: config.twilio.templates.repeated,
      templateRecovered: config.twilio.templates.recovered,
    }));
  }, [configRemote.data]);

  const field = (name: keyof ConfigForm) => ({
    value: form[name],
    onChange: (event: ChangeEvent<HTMLInputElement>) => setForm((current) => ({ ...current, [name]: event.target.value })),
  });

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setMessage(null);
    try {
      await apiRequest("/api/v1/admin/notifications/config", {
        method: "PUT",
        csrfToken,
        body: {
          recipient: { email: form.email, smsPhone: form.smsPhone, whatsappPhone: form.whatsappPhone },
          summary: { time: form.summaryTime, timezone: form.timezone },
          smtp: { host: form.smtpHost, port: Number(form.smtpPort), username: form.smtpUser, password: form.smtpPassword || undefined, from: form.smtpFrom },
          twilio: { accountSid: form.twilioAccountSid, authToken: form.twilioAuthToken || undefined, smsFrom: form.twilioSmsFrom, whatsappFrom: form.twilioWhatsappFrom, templates: { opened: form.templateOpened, repeated: form.templateRepeated, recovered: form.templateRecovered } },
        },
      });
      setMessage({ tone: "success", title: "Configuración guardada", body: "Los secretos ingresados quedaron protegidos y los campos se limpiaron." });
      setForm((current) => ({ ...current, smtpPassword: "", twilioAuthToken: "" }));
      configRemote.reload();
    } catch (error) {
      setMessage({ tone: "danger", title: "No se pudo guardar", body: error instanceof Error ? error.message : "Intentá nuevamente." });
    } finally { setSaving(false); }
  };

  const sendTest = async (channel: DeliveryChannel) => {
    setTesting(channel);
    setMessage(null);
    try {
      await apiRequest("/api/v1/admin/notifications/test", { method: "POST", csrfToken, body: { channels: [channel] } });
      setMessage({ tone: "success", title: "Prueba encolada", body: `El canal ${CHANNEL_LABELS[channel]} procesará un mensaje de prueba.` });
      window.setTimeout(deliveries.reload, 800);
    } catch (error) {
      setMessage({ tone: "danger", title: "La prueba falló", body: error instanceof Error ? error.message : "Intentá nuevamente." });
    } finally { setTesting(null); }
  };

  const addPush = async () => {
    const key = configRemote.data?.vapidPublicKey;
    if (!key) { setMessage({ tone: "warning", title: "Push no configurado", body: "Primero configurá una clave VAPID en el servidor." }); return; }
    setPushBusy(true);
    try {
      await subscribeToPush(key, pushLabel, csrfToken);
      setMessage({ tone: "success", title: "Navegador suscripto", body: "Este dispositivo puede recibir alertas push." });
      configRemote.reload();
    } catch (error) { setMessage({ tone: "danger", title: "No se pudo activar Push", body: error instanceof Error ? error.message : "Revisá los permisos del navegador." }); }
    finally { setPushBusy(false); }
  };

  const removePush = async (id: string) => {
    try {
      await apiRequest(`/api/v1/push-subscriptions/${encodeURIComponent(id)}`, { method: "DELETE", csrfToken });
      configRemote.reload();
    } catch (error) { setMessage({ tone: "danger", title: "No se pudo quitar", body: error instanceof Error ? error.message : "Intentá nuevamente." }); }
  };

  return (
    <div className="page">
      <PageHeader eyebrow="Administración" title="Notificaciones" description={SHOW_TWILIO_ADMIN ? "Definí un destinatario, prepará los canales y verificá cada entrega." : "Configurá el correo, los avisos Push web y la trazabilidad visible de las entregas."} actions={<Button variant="secondary" size="small" onClick={deliveries.reload} loading={deliveries.refreshing}><RefreshCw size={15} /> Actualizar entregas</Button>} />
      {configRemote.loading && !configRemote.data ? <LoadingBlock rows={7} /> : null}
      {configRemote.error && !configRemote.data ? <ErrorState error={configRemote.error} onRetry={configRemote.reload} /> : null}
      {configRemote.data ? (
        <>
          <InlineNotice
            tone={configRemote.data.mode === "mock" ? "info" : "warning"}
            title={notificationsDisabled ? "Notificaciones deshabilitadas" : configRemote.data.mode === "mock" ? "Modo de prueba local" : "Canales de producción"}
          >
            {notificationsDisabled
              ? "Centro Monitoreo Hikdos no genera entregas ni acumula mensajes pendientes. Los canales podrán configurarse y activarse más adelante sin enviar incidentes anteriores."
              : configRemote.data.mode === "mock"
                ? SHOW_TWILIO_ADMIN
                  ? "Los mensajes se guardan en la bandeja inferior. No se envía correo, SMS, WhatsApp ni Push real."
                  : "Los mensajes se guardan en la bandeja inferior. No se envía correo ni Push real."
                : "Las pruebas pueden generar mensajes reales al destinatario configurado."}
          </InlineNotice>
          {!SHOW_TWILIO_ADMIN ? <InlineNotice tone="info" title="Panel simplificado">En esta etapa se muestran únicamente correo y Push web. Esta vista no cambia los envíos de otros canales que ya estén configurados.</InlineNotice> : null}
          {message ? <InlineNotice tone={message.tone} title={message.title}>{message.body}</InlineNotice> : null}
          <form onSubmit={save}>
            <fieldset disabled={notificationsDisabled} className="notification-settings-fieldset">
            <section className="notification-layout">
              <Card>
                <div className="card-heading"><div><p className="eyebrow">Perfil único · v1</p><h2>Destinatario</h2></div><ShieldCheck size={22} /></div>
                <div className="form-grid">
                  <Field label="Correo electrónico"><span className="input-with-icon"><Mail size={16} /><input type="email" {...field("email")} placeholder="operaciones@ejemplo.com" /></span></Field>
                  {SHOW_TWILIO_ADMIN ? <>
                    <Field label="Teléfono para SMS" hint="Formato internacional E.164."><span className="input-with-icon"><Phone size={16} /><input type="tel" {...field("smsPhone")} placeholder="+5491112345678" /></span></Field>
                    <Field label="Teléfono para WhatsApp" hint="Debe haber aceptado recibir mensajes."><span className="input-with-icon"><MessageCircle size={16} /><input type="tel" {...field("whatsappPhone")} placeholder="+5491112345678" /></span></Field>
                  </> : null}
                  <Field label="Resumen diario" hint="Se ejecuta a la hora en punto."><span className="input-with-icon"><Clock3 size={16} /><input type="time" step="3600" {...field("summaryTime")} /></span></Field>
                </div>
                <Field label="Zona horaria"><input {...field("timezone")} /></Field>
              </Card>
              <Card>
                <div className="card-heading"><div><p className="eyebrow">Política activa</p><h2>{SHOW_TWILIO_ADMIN ? "Rutas por severidad" : "Rutas visibles por severidad"}</h2></div><BellRing size={22} /></div>
                <dl className="info-list">
                  <div><dt>Alta / Desastre</dt><dd>{SHOW_TWILIO_ADMIN ? "Correo, SMS, WhatsApp y Push" : "Todos los canales configurados"}</dd></div>
                  <div><dt>Recordatorio urgente</dt><dd>Cada 30 minutos hasta atender o recuperar</dd></div>
                  <div><dt>Media</dt><dd>Correo y Push, sin repetición</dd></div>
                  <div><dt>Advertencia e información</dt><dd>Resumen diario</dd></div>
                  <div><dt>Recuperación</dt><dd>Los mismos canales de la alerta inicial</dd></div>
                </dl>
                <InlineNotice tone="info" title="Reglas operativas v1">El reconocimiento detiene recordatorios, pero el problema sigue visible hasta que Zabbix informe su recuperación.</InlineNotice>
              </Card>
              <Card>
                <div className="card-heading"><div><p className="eyebrow">Canal</p><h2>Correo SMTP</h2></div><Mail size={22} /></div>
                <div className="form-grid"><Field label="Servidor"><input {...field("smtpHost")} placeholder="smtp.ejemplo.com" /></Field><Field label="Puerto"><input type="number" min="1" max="65535" {...field("smtpPort")} /></Field><Field label="Usuario"><input {...field("smtpUser")} autoComplete="off" /></Field><Field label="Contraseña" hint="Dejá vacío para conservar la actual."><span className="input-with-icon"><KeyRound size={16} /><input type="password" {...field("smtpPassword")} autoComplete="new-password" /></span></Field><Field label="Remitente"><input type="email" {...field("smtpFrom")} placeholder="alertas@ejemplo.com" /></Field></div>
                <ChannelFooter channel="email" config={configRemote.data} testing={testing} onTest={sendTest} />
              </Card>
              {SHOW_TWILIO_ADMIN ? <Card>
                <div className="card-heading"><div><p className="eyebrow">Canales</p><h2>Twilio SMS y WhatsApp</h2></div><Smartphone size={22} /></div>
                <div className="form-grid"><Field label="Account SID"><input {...field("twilioAccountSid")} autoComplete="off" /></Field><Field label="Auth Token" hint="Dejá vacío para conservar el actual."><span className="input-with-icon"><KeyRound size={16} /><input type="password" {...field("twilioAuthToken")} autoComplete="new-password" /></span></Field><Field label="Remitente SMS"><input {...field("twilioSmsFrom")} placeholder="+10000000000" /></Field><Field label="Remitente WhatsApp"><input {...field("twilioWhatsappFrom")} placeholder="+10000000000" /></Field></div>
                <details className="advanced-settings"><summary>Plantillas aprobadas de WhatsApp</summary><div className="form-grid"><Field label="Problema abierto"><input {...field("templateOpened")} placeholder="HX…" /></Field><Field label="Recordatorio"><input {...field("templateRepeated")} placeholder="HX…" /></Field><Field label="Problema recuperado"><input {...field("templateRecovered")} placeholder="HX…" /></Field></div></details>
                <div className="channel-tests"><ChannelFooter channel="sms" config={configRemote.data} testing={testing} onTest={sendTest} /><ChannelFooter channel="whatsapp" config={configRemote.data} testing={testing} onTest={sendTest} /></div>
              </Card> : null}
              <Card>
                <div className="card-heading"><div><p className="eyebrow">Este dispositivo</p><h2>Avisos Push web</h2></div><MonitorSmartphone size={22} /></div>
                {!supportsWebPush() ? <InlineNotice tone="warning" title="Navegador no compatible">Abrí Centro Monitoreo Hikdos en un navegador moderno con HTTPS para activar Push.</InlineNotice> : null}
                <Field label="Nombre del dispositivo"><input value={pushLabel} onChange={(event) => setPushLabel(event.target.value)} /></Field>
                <Button type="button" variant="secondary" loading={pushBusy} disabled={!supportsWebPush()} onClick={() => void addPush()}><BellRing size={16} /> Activar en este navegador</Button>
                <div className="subscription-list">{configRemote.data.pushSubscriptions.map((subscription) => <div key={subscription.id}><span className="subscription-list__icon"><MonitorSmartphone size={17} /></span><div><strong>{subscription.label}</strong><DataAge value={subscription.createdAt} prefix="Agregado" /></div><button type="button" aria-label={`Quitar ${subscription.label}`} onClick={() => void removePush(subscription.id)}><Trash2 size={16} /></button></div>)}</div>
              </Card>
            </section>
            <div className="sticky-save"><span>{notificationsDisabled ? "La configuración queda bloqueada mientras los avisos están deshabilitados." : "Los secretos nunca vuelven a mostrarse después de guardar."}</span><Button type="submit" loading={saving}><Save size={16} /> Guardar configuración</Button></div>
            </fieldset>
          </form>
        </>
      ) : null}

      <Card className="delivery-card">
        <div className="card-heading"><div><p className="eyebrow">Trazabilidad</p><h2>Últimas entregas</h2></div><Send size={22} /></div>
        {deliveries.loading && !deliveries.data ? <LoadingBlock rows={5} /> : null}
        {deliveries.error && !deliveries.data ? <ErrorState error={deliveries.error} onRetry={deliveries.reload} /> : null}
        {visibleDeliveries.length ? <DeliveryTable items={visibleDeliveries} /> : null}
        {deliveries.data && !visibleDeliveries.length ? <EmptyState icon={Send} title="Todavía no hay entregas visibles" description="Usá un botón de prueba para comprobar el flujo sin esperar una alerta." /> : null}
      </Card>
    </div>
  );
}

function ChannelFooter({ channel, config, testing, onTest }: { channel: DeliveryChannel; config: NotificationConfig; testing: DeliveryChannel | null; onTest: (channel: DeliveryChannel) => Promise<void> }) {
  const configured = config.channels[channel].configured;
  return <div className="channel-footer"><span className={configured ? "configured" : "not-configured"}>{configured ? <Check size={14} /> : <TriangleAlert size={14} />}{configured ? "Configurado" : "Falta configurar"}</span><Button type="button" variant="ghost" size="small" loading={testing === channel} onClick={() => void onTest(channel)}><Send size={14} /> Probar {CHANNEL_LABELS[channel]}</Button></div>;
}

function DeliveryTable({ items }: { items: NotificationDelivery[] }) {
  return <div className="delivery-table" role="table" aria-label="Entregas de notificaciones"><div className="delivery-table__head" role="row"><span role="columnheader">Canal</span><span role="columnheader">Mensaje</span><span role="columnheader">Destino</span><span role="columnheader">Estado</span><span role="columnheader">Fecha</span></div>{items.map((delivery) => <div className="delivery-table__row" role="row" key={delivery.id}><span role="cell"><ChannelIcon channel={delivery.channel} />{CHANNEL_LABELS[delivery.channel]}</span><span role="cell"><strong>{delivery.subject ?? deliveryKindLabel(delivery.kind)}</strong>{delivery.error ? <small>{delivery.error}</small> : null}</span><span role="cell">{delivery.destinationDisplay ?? "Destinatario configurado"}</span><span role="cell" className={`delivery-status delivery-status--${delivery.status}`}>{DELIVERY_LABELS[delivery.status]}</span><span role="cell"><DataAge value={delivery.attemptedAt ?? delivery.createdAt} /></span></div>)}</div>;
}

function ChannelIcon({ channel }: { channel: DeliveryChannel }) {
  if (channel === "email") return <Mail size={15} />;
  if (channel === "sms") return <Phone size={15} />;
  if (channel === "whatsapp") return <MessageCircle size={15} />;
  return <BellRing size={15} />;
}

function deliveryKindLabel(kind: string | undefined): string {
  if (kind === "test") return "Mensaje de prueba";
  if (kind === "recovered") return "Problema recuperado";
  if (kind === "repeat") return "Recordatorio de problema";
  if (kind === "summary") return "Resumen diario";
  return "Alerta de monitoreo";
}
